import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Award, ShieldCheck, AlertTriangle, CheckCircle, XCircle,
  DollarSign, TrendingUp, Activity, Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { KPICard } from "@/components/KPICard";
import { getSharedData } from "@/lib/api";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

export default function SharedView() {
  const { linkId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getSharedData(linkId);
        if (res.data.error) {
          setError(res.data.error);
        } else {
          setData(res.data);
        }
      } catch (e) {
        setError("Failed to load shared analysis");
      }
      setLoading(false);
    };
    fetchData();
  }, [linkId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center" data-testid="shared-error">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
            Link Not Found
          </h2>
          <p className="text-[#57534E]">{error}</p>
        </div>
      </div>
    );
  }

  const { cash_flow_report: cf, comparison, recommendation: rec } = data;
  const avg = cf?.averages || {};
  const months = cf?.monthly_summaries || [];

  const chartData = months.map((m) => ({
    month: m.month,
    Revenue: m.true_revenue,
    "Net Cash Flow": m.net_cash_flow,
  }));

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="shared-view">
      {/* Header */}
      <header className="bg-[#1A362D] text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold tracking-tight">
              {data.title || "Loan Analysis Report"}
            </h1>
            <p className="text-[#8A9A86] text-sm mt-1 font-['IBM_Plex_Sans']">
              Prepared by Loan Cash Flow Analyzer
            </p>
          </div>
          <Button
            data-testid="print-shared-btn"
            onClick={() => window.print()}
            variant="ghost"
            className="text-white hover:bg-white/10 no-print"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Cash Flow KPIs */}
        <section>
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
            Cash Flow Overview
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard title="Avg Monthly Deposits" value={fmtMoney(avg.avg_monthly_gross_deposits)} icon={DollarSign} />
            <KPICard title="Operating Cash Flow" value={fmtMoney(avg.avg_monthly_operating_cash_flow)} icon={TrendingUp}
              variant={avg.avg_monthly_operating_cash_flow > 0 ? "success" : "danger"} />
            <KPICard title="Free Cash Flow" value={fmtMoney(avg.free_cash_flow)} icon={ShieldCheck}
              subtitle="Available for new debt"
              variant={avg.free_cash_flow > 5000 ? "success" : avg.free_cash_flow > 0 ? "warning" : "danger"} />
            <KPICard title="DSCR" value={avg.dscr >= 99 ? "N/A" : `${avg.dscr}x`} icon={Activity}
              subtitle={avg.dscr >= 1.5 ? "Healthy" : avg.dscr >= 1.15 ? "Tight" : "At Risk"}
              variant={avg.dscr >= 1.5 ? "success" : avg.dscr >= 1.15 ? "warning" : "danger"} />
          </div>
        </section>

        {/* Chart */}
        {chartData.length > 0 && (
          <section className="bg-white border border-[#E7E5DF] rounded-md p-6">
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
              Revenue &amp; Cash Flow Trends
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#78716C" }} />
                <YAxis tick={{ fontSize: 12, fill: "#78716C" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`$${v.toLocaleString()}`]} contentStyle={{ borderRadius: 6, border: "1px solid #E7E5DF", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="Revenue" stroke="#1A362D" fill="#1A362D" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="Net Cash Flow" stroke="#8A9A86" fill="#8A9A86" fillOpacity={0.08} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </section>
        )}

        {/* Recommendation */}
        {rec && rec.best_offer && (
          <section>
            <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
              Recommendation
            </h2>
            <div className="bg-white border border-[#E7E5DF] rounded-md p-6 sm:p-8 mb-4">
              <p className="text-[#57534E] leading-relaxed">{rec.summary}</p>
            </div>
            <div className={`border-2 rounded-md p-6 ${
              rec.best_offer.recommendation === "Good Fit" ? "border-[#2D4A3E] bg-[#2D4A3E]/5" :
              rec.best_offer.recommendation === "Borderline" ? "border-[#E3B505] bg-[#E3B505]/5" :
              "border-[#E07A5F] bg-[#E07A5F]/5"
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-[#1A362D]" />
                <h4 className="font-['Playfair_Display'] font-semibold text-[#1C1917]">
                  Best Option: {rec.best_offer.lender_name}
                </h4>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">Product</p>
                  <p className="font-semibold mt-1">{rec.best_offer.product_type}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">Monthly Payment</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] mt-1">{fmtMoney(rec.best_offer.monthly_payment)}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">DSCR After</p>
                  <p className="font-semibold font-['IBM_Plex_Mono'] mt-1">{rec.best_offer.new_dscr >= 99 ? "N/A" : `${rec.best_offer.new_dscr}x`}</p>
                </div>
                <div>
                  <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">Risk Level</p>
                  <p className={`font-semibold mt-1 ${
                    rec.best_offer.risk_level === "Low" ? "text-[#2D4A3E]" :
                    rec.best_offer.risk_level === "Moderate" ? "text-[#E3B505]" : "text-[#E07A5F]"
                  }`}>{rec.best_offer.risk_level}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Loan Comparison Table */}
        {comparison?.length > 0 && (
          <section className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
            <div className="p-4 border-b border-[#E7E5DF]">
              <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
                All Loan Offers Compared
              </h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F1EFEA]">
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">#</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Lender</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Loan Amt</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Mo. Payment</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Total Cost</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">DSCR</TableHead>
                    <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Verdict</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparison.map((o) => (
                    <TableRow key={o.id} className="table-row-hover">
                      <TableCell className="font-['IBM_Plex_Mono'] font-bold text-[#1A362D] py-4">#{o.rank}</TableCell>
                      <TableCell className="font-semibold text-[#1C1917] py-4">
                        {o.lender_name}
                        <span className="block text-xs text-[#78716C] font-normal">{o.product_type}</span>
                      </TableCell>
                      <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right py-4">{fmtMoney(o.loan_amount)}</TableCell>
                      <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right font-semibold py-4">{fmtMoney(o.monthly_debt_service)}</TableCell>
                      <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#E07A5F] font-semibold py-4">{fmtMoney(o.total_financing_cost)}</TableCell>
                      <TableCell className={`font-['IBM_Plex_Mono'] text-sm text-right font-semibold py-4 ${
                        o.new_dscr >= 1.5 ? "text-[#2D4A3E]" : o.new_dscr >= 1.15 ? "text-[#E3B505]" : "text-[#E07A5F]"
                      }`}>{o.new_dscr >= 99 ? "N/A" : `${o.new_dscr}x`}</TableCell>
                      <TableCell className="py-4">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${
                          o.recommendation === "Good Fit" ? "bg-[#2D4A3E] text-white" :
                          o.recommendation === "Borderline" ? "bg-[#E3B505] text-[#1C1917]" :
                          "bg-[#E07A5F] text-white"
                        }`}>
                          {o.recommendation === "Good Fit" ? <CheckCircle className="w-3 h-3" /> :
                           o.recommendation === "Borderline" ? <AlertTriangle className="w-3 h-3" /> :
                           <XCircle className="w-3 h-3" />}
                          {o.recommendation}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {/* Concerns */}
        {rec?.concerns?.length > 0 && (
          <section className="bg-white border border-[#E07A5F]/30 rounded-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#E07A5F]" />
              <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#E07A5F]">
                Important Considerations
              </h3>
            </div>
            <ul className="space-y-2">
              {rec.concerns.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#57534E]">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E07A5F] flex-shrink-0" />{c}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center py-6 text-xs text-[#78716C]">
          <p>Generated by Loan Cash Flow Analyzer &middot; This report is for informational purposes only.</p>
        </footer>
      </main>
    </div>
  );
}
